import { useMemo, useState } from 'react';
import { Filter, Search, X } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { useTranslation } from '@/hooks/useTranslation';
import { cn } from '@/lib/utils';
import type { EntityOption } from '@/hooks/useEntityOptions';

interface ColumnFilterProps {
  /** Column header label — used in aria-label + popover subheading. */
  columnHeader: string;
  /**
   * Unique stringified cell values present in the current data for this
   * column. Sorted by label (case-insensitive) before rendering.
   */
  uniqueValues: string[];
  /**
   * Optional entity options for the column. When provided, UUIDs in
   * `uniqueValues` are resolved to the entity's `label` for display;
   * the underlying filter key stays the raw value.
   */
  entityOptions?: EntityOption[];
  /**
   * Column-supplied label resolver (`ColumnConfig.displayValue`) for
   * columns whose raw values are ids rendered through a custom
   * `cellRenderer`. Takes precedence over `entityOptions`.
   */
  resolveLabel?: (value: string) => string;
  /** Currently selected values (the "keep" set). Empty = no filter. */
  selected: Set<string>;
  /** Fires on every toggle / clear so filters auto-apply. */
  onChange: (next: Set<string>) => void;
}

// Unicode Private-Use-Area sentinel — guaranteed not to appear in real
// string data, so it can't collide with a literal cell value of
// `"__empty__"` or similar. Any empty / null cell is normalised to this
// key at filter time.
const EMPTY_LABEL_KEY = '\uE000__filter_empty__\uE000';

/**
 * Popover filter control rendered in the column header. Multi-select
 * checkbox list of unique column values; auto-applies on toggle.
 */
export function ColumnFilter({
  columnHeader,
  uniqueValues,
  entityOptions,
  resolveLabel,
  selected,
  onChange,
}: ColumnFilterProps) {
  const { t } = useTranslation('spreadsheet');
  const [open, setOpen] = useState(false);
  const [valueSearch, setValueSearch] = useState('');

  const hasActiveFilter = selected.size > 0;

  // Build { value, label } pairs — resolve entity UUIDs, substitute a
  // "(empty)" label for missing values so users can still filter on
  // blank cells.
  const items = useMemo(() => {
    const entityMap = entityOptions
      ? new Map(entityOptions.map((o) => [String(o.value), o.label]))
      : null;
    return uniqueValues
      .map((value) => {
        if (value === EMPTY_LABEL_KEY) {
          return { value, label: t('filter.emptyValue') };
        }
        // `||` (not `??`): a resolver step yielding '' is a MISSING label,
        // not a real one — fall through to the entity map, then the raw
        // value, so no filter row ever renders blank. Genuinely-empty cells
        // never reach this branch (normalised to EMPTY_LABEL_KEY above).
        const label = resolveLabel?.(value) || entityMap?.get(value) || value;
        return { value, label };
      })
      .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
  }, [uniqueValues, entityOptions, resolveLabel, t]);

  const filteredItems = useMemo(() => {
    const q = valueSearch.trim().toLowerCase();
    if (!q) return items;
    return items.filter((it) => it.label.toLowerCase().includes(q));
  }, [items, valueSearch]);

  const toggle = (value: string) => {
    const next = new Set(selected);
    if (next.has(value)) next.delete(value);
    else next.add(value);
    onChange(next);
  };

  const clear = () => {
    onChange(new Set());
    setValueSearch('');
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={t('filter.label') + ': ' + columnHeader}
          title={t('filter.label')}
          className={cn(
            'inline-flex items-center justify-center rounded-sm p-0.5 transition-colors',
            'opacity-0 group-hover:opacity-100 focus-visible:opacity-100',
            hasActiveFilter && 'opacity-100 text-primary',
            !hasActiveFilter && 'text-muted-foreground hover:text-foreground',
          )}
          onClick={(e) => e.stopPropagation()}
        >
          <Filter className="h-3.5 w-3.5" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-64 p-0"
        align="start"
        onKeyDown={(e) => e.stopPropagation()}
      >
        <div className="p-2 border-b">
          <div className="relative">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
            <Input
              type="search"
              value={valueSearch}
              onChange={(e) => setValueSearch(e.target.value)}
              placeholder={t('filter.searchPlaceholder')}
              className="pl-7 h-8 text-sm"
              aria-label={t('filter.searchPlaceholder')}
            />
          </div>
        </div>
        {/* Native scroll container — NOT Radix ScrollArea: its viewport is
            `h-full`, which doesn't resolve against a max-height-only root,
            so content past the cap clips without ever scrolling. */}
        <div className="max-h-64 overflow-y-auto">
          <div className="p-1">
            {filteredItems.length === 0 ? (
              <div className="px-3 py-6 text-center text-sm text-muted-foreground">
                {t('filter.noMatches')}
              </div>
            ) : (
              filteredItems.map((it) => {
                const checked = selected.has(it.value);
                return (
                  <label
                    key={it.value}
                    className="flex items-center gap-2 px-2 py-1.5 rounded-sm hover:bg-accent cursor-pointer"
                  >
                    <Checkbox
                      checked={checked}
                      onCheckedChange={() => toggle(it.value)}
                      aria-label={it.label}
                    />
                    <span className="text-sm truncate flex-1">{it.label}</span>
                  </label>
                );
              })
            )}
          </div>
        </div>
        <div className="flex items-center justify-between border-t p-2">
          <span className="text-xs text-muted-foreground">
            {selected.size > 0
              ? t('filter.appliedSummary', { count: selected.size })
              : ''}
          </span>
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-xs"
              onClick={clear}
              disabled={!hasActiveFilter}
            >
              <X className="h-3 w-3 mr-1" />
              {t('filter.clear')}
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

/** Sentinel value the filter uses for missing cell values. */
export const FILTER_EMPTY_VALUE = EMPTY_LABEL_KEY;
